import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  BarChart3,
  Building2,
  Calendar,
  Check,
  ChevronRight,
  FileText,
  Globe,
  LayoutDashboard,
  LineChart,
  MapPin,
  MessageSquare,
  Search,
  Shield,
  Star,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import SiteNavBar from '@/components/SiteNavBar';

const RevealOnScroll = ({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTimeout(() => setIsVisible(true), delay * 100); },
      { threshold: 0.1, rootMargin: '50px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${className}`}
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(24px)' }}>
      {children}
    </div>
  );
};

const ParticleField = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(30)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 rounded-full bg-cyan-500/30"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 2}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
        50% { transform: translateY(-20px) translateX(10px); opacity: 0.8; }
      }
    `}</style>
  </div>
);

const FEATURES = [
  {
    icon: Star,
    title: 'Review Management',
    description: 'Monitor and respond to every Google review. AI-powered response suggestions, sentiment analysis, and auto-responder for fast replies.',
    color: '#f59e0b',
    gradient: 'from-amber-500 to-orange-400',
  },
  {
    icon: TrendingUp,
    title: 'Keyword Rank Tracking',
    description: 'Track how you rank for target keywords on Google Maps and Search. Geographic grid analysis, competitor tracking, and trend reports.',
    color: '#3b82f6',
    gradient: 'from-blue-500 to-cyan-400',
  },
  {
    icon: Shield,
    title: 'Profile Protection',
    description: '24/7 monitoring for unauthorized changes to your business profile. Instant alerts if someone edits your hours, phone, address, or categories.',
    color: '#10b981',
    gradient: 'from-emerald-500 to-teal-400',
  },
  {
    icon: Calendar,
    title: 'Content Publishing',
    description: 'Create and schedule Google Business posts. AI-generated content ideas, image suggestions, seasonal planning, and publishing calendar.',
    color: '#8b5cf6',
    gradient: 'from-violet-500 to-fuchsia-400',
  },
  {
    icon: BarChart3,
    title: 'Performance Analytics',
    description: 'Track views, clicks, calls, direction requests, and bookings. Search vs Maps breakdown, mobile vs desktop, and month-over-month trends.',
    color: '#ef4444',
    gradient: 'from-red-500 to-orange-400',
  },
  {
    icon: Globe,
    title: 'Microsite Builder',
    description: 'Create a branded landing page or store locator for your business. Custom domains, auto-generated SEO content, and Google Maps integration.',
    color: '#14b8a6',
    gradient: 'from-teal-500 to-emerald-400',
  },
  {
    icon: FileText,
    title: 'Reports & Insights',
    description: 'Generate professional PDF reports with key metrics. Monthly summaries, competitor comparisons, and actionable recommendations.',
    color: '#6366f1',
    gradient: 'from-indigo-500 to-violet-400',
  },
  {
    icon: Search,
    title: 'SEO Audit & Optimization',
    description: 'Complete GBP profile audit with scoring. Category optimization, service listing management, attribute suggestions, and description improvements.',
    color: '#06b6d4',
    gradient: 'from-cyan-500 to-blue-400',
  },
  {
    icon: Building2,
    title: 'Multi-Location Management',
    description: 'Manage multiple business locations from a single dashboard. Per-location analytics, bulk operations, and workspace organization.',
    color: '#ec4899',
    gradient: 'from-pink-500 to-rose-400',
  },
];

declare global {
  interface Window {
    __GEO_COUNTRY__?: string;
  }
}

const FlyDIYPage = () => {
  const [isIndianUser, setIsIndianUser] = useState(true);

  useEffect(() => {
    const VERCEL_EDGE_REGIONS = ['SG', 'HK', 'JP', 'KR', 'AU', 'NZ'];

    const detectCountry = async () => {
      const geoCountry = typeof window !== 'undefined' ? window.__GEO_COUNTRY__ : undefined;

      if (geoCountry && VERCEL_EDGE_REGIONS.includes(geoCountry)) {
        try {
          const response = await fetch('/api/geo');
          const data = await response.json();
          if (data.countryCode && data.countryCode !== 'IN') setIsIndianUser(false);
          return;
        } catch { return; }
      }

      if (geoCountry && geoCountry !== 'IN') setIsIndianUser(false);
    };

    detectCountry();
  }, []);

  return (
    <>
      <Helmet>
        <title>Fly DIY | Self-Service Local SEO Dashboard</title>
        <meta name="description" content="Manage your Google Business Profile yourself with Fly DIY. Track rankings, respond to reviews, publish content, protect your profile, and generate reports — all from one dashboard." />
        <meta property="og:title" content="Fly DIY | Self-Service Local SEO Dashboard" />
        <meta property="og:description" content="Full-featured GBP management dashboard. Rankings, reviews, content, protection, and analytics — do it yourself with Fly." />
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-white">
        <SiteNavBar />

        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
          <ParticleField />

          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '64px 64px',
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-b from-cyan-950/20 via-transparent to-gray-950" />

          <div className="relative z-10 container mx-auto px-4 text-center">
            <RevealOnScroll>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-8">
                <LayoutDashboard className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-cyan-300">Self-Service Dashboard</span>
              </div>

              <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400">
                  Fly DIY
                </span>
                <br />
                <span className="text-white">Your GBP, Your Way</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-4 leading-relaxed">
                A full-featured dashboard to manage your Google Business Profile yourself.
              </p>
              <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-8">
                <span className="text-cyan-400">Rankings</span>,
                <span className="text-blue-400"> reviews</span>,
                <span className="text-violet-400"> content</span>,
                <span className="text-emerald-400"> protection</span>,
                <span className="text-amber-400"> analytics</span>
                — everything you need in one place.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link to="/fly-app">
                  <Button size="lg" className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/25">
                    <LayoutDashboard className="w-5 h-5 mr-2" />
                    Login to Fly DIY
                  </Button>
                </Link>
                <a href="#features">
                  <Button size="lg" className="bg-transparent border border-gray-600 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-xl">
                    See All Features
                  </Button>
                </a>
              </div>

              <div className="flex flex-wrap gap-6 justify-center text-sm text-gray-500">
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-cyan-400" /> No credit card required</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-cyan-400" /> Connect your GBP in minutes</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-cyan-400" /> Multi-location support</div>
              </div>
            </RevealOnScroll>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronRight className="w-6 h-6 text-gray-600 rotate-90" />
          </div>
        </section>

        {/* Features Grid */}
        <section id="features" className="py-24 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-cyan-950/10 to-gray-950" />

          <div className="relative container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                    Everything You Need
                  </span>
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  One dashboard for complete Google Business Profile management
                </p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {FEATURES.map((feature, index) => (
                <RevealOnScroll key={index} delay={index}>
                  <div className="relative group h-full">
                    <div className="absolute -inset-0.5 rounded-2xl blur-lg opacity-0 group-hover:opacity-40 transition-opacity duration-500"
                      style={{ background: `linear-gradient(135deg, ${feature.color}40, transparent)` }} />
                    <div className="relative bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-800 hover:border-gray-700 transition-all duration-300 p-6 h-full">
                      <div
                        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} p-2.5 mb-4 shadow-lg`}
                        style={{ boxShadow: `0 8px 32px ${feature.color}40` }}
                      >
                        <feature.icon className="w-full h-full text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 relative bg-gradient-to-b from-gray-950 via-gray-900/50 to-gray-950">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Get Started in Minutes</h2>
                <p className="text-gray-400 text-lg">Three simple steps to take control of your Local SEO</p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  step: '01',
                  title: 'Sign Up & Connect',
                  description: 'Create your account and connect your Google Business Profile with one-click secure authorization.',
                  icon: Users,
                  color: 'cyan',
                },
                {
                  step: '02',
                  title: 'Explore Your Dashboard',
                  description: 'See your reviews, rankings, analytics, and profile health all in one place. Get instant audit and recommendations.',
                  icon: LayoutDashboard,
                  color: 'blue',
                },
                {
                  step: '03',
                  title: 'Take Action',
                  description: 'Respond to reviews, publish content, track rankings, set up protection alerts, and generate reports — all from Fly DIY.',
                  icon: Zap,
                  color: 'violet',
                },
              ].map((item, index) => (
                <RevealOnScroll key={index} delay={index}>
                  <div className="relative group">
                    <div className={`absolute -inset-0.5 bg-gradient-to-r from-${item.color}-600/30 to-transparent rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity`} />
                    <div className="relative bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-800 p-8 h-full">
                      <div className={`text-6xl font-black text-${item.color}-500/20 mb-4`}>{item.step}</div>
                      <div className={`w-12 h-12 rounded-xl bg-${item.color}-500/10 p-2.5 mb-4`}>
                        <item.icon className={`w-full h-full text-${item.color}-400`} />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                      <p className="text-gray-400">{item.description}</p>
                    </div>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-24 relative">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Simple Pricing</h2>
                <p className="text-gray-400 text-lg mb-12">Full access to all features. No hidden fees.</p>

                <div className="relative inline-block">
                  <div className="absolute -inset-1 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-3xl blur-lg opacity-50" />
                  <div className="relative bg-gray-900 rounded-3xl border border-gray-800 p-8 md:p-12">
                    <div className="flex items-baseline justify-center gap-2 mb-2">
                      <span className="text-5xl md:text-6xl font-black text-white">{isIndianUser ? '₹999' : '$14.99'}</span>
                      <span className="text-gray-400 text-lg">/location/month</span>
                    </div>
                    <p className="text-gray-500 mb-8">Billed monthly. Cancel anytime.</p>

                    <ul className="space-y-4 text-left max-w-sm mx-auto mb-8">
                      {[
                        'Full dashboard access',
                        'Review management & AI responses',
                        'Keyword rank tracking',
                        'Profile protection & alerts',
                        'Content creation & publishing',
                        'Performance analytics & reports',
                        'Microsite builder',
                        'Multi-location support',
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-gray-300">
                          <Check className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <Link to="/fly-app">
                      <Button size="lg" className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-8 py-6 text-lg rounded-xl w-full">
                        Login to Fly DIY
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </RevealOnScroll>
          </div>
        </section>

        {/* Not for DIY? */}
        <section className="py-24 relative bg-gradient-to-b from-gray-950 via-gray-900/50 to-gray-950">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Prefer Someone Else to Handle It?</h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  If you'd rather not manage your GBP yourself, check out our other options
                </p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <RevealOnScroll delay={0}>
                <Link to="/managed" className="block h-full group">
                  <div className="relative h-full">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600/40 to-emerald-600/40 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative h-full bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-800 hover:border-green-500/50 transition-all duration-300 p-8">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 p-2.5 mb-4">
                        <Users className="w-full h-full text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Managed Local SEO</h3>
                      <p className="text-gray-400 text-sm mb-4">Let Fly's expert team handle everything — reviews, rankings, content, and reports. You do nothing.</p>
                      <div className="flex items-center text-green-400 text-sm font-medium group-hover:translate-x-2 transition-transform">
                        Learn more <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </div>
                  </div>
                </Link>
              </RevealOnScroll>

              <RevealOnScroll delay={1}>
                <Link to="/gmb-whatsapp-assistant" className="block h-full group">
                  <div className="relative h-full">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600/40 to-emerald-600/40 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative h-full bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-800 hover:border-green-500/50 transition-all duration-300 p-8">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 p-2.5 mb-4">
                        <MessageSquare className="w-full h-full text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Fly Lite — WhatsApp Assistant</h3>
                      <p className="text-gray-400 text-sm mb-4">Manage your GBP via WhatsApp. Review alerts, AI responses, profile protection — all in your chat app.</p>
                      <div className="flex items-center text-green-400 text-sm font-medium group-hover:translate-x-2 transition-transform">
                        Learn more <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </div>
                  </div>
                </Link>
              </RevealOnScroll>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-950/50 via-blue-950/50 to-violet-950/50" />
          <ParticleField />

          <div className="relative container mx-auto px-4 text-center">
            <RevealOnScroll>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Take Control of Your Local SEO
              </h2>
              <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
                Connect your Google Business Profile and start managing everything from one dashboard.
              </p>
              <Link to="/fly-app">
                <Button size="lg" className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-10 py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/25">
                  <LayoutDashboard className="w-5 h-5 mr-2" />
                  Login to Fly DIY
                </Button>
              </Link>
            </RevealOnScroll>
          </div>
        </section>

        <Footer variant="dark" />
      </div>
    </>
  );
};

export default FlyDIYPage;
